import { useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Alert } from '@/components/ui/alert'
import { ApiError, getJSON } from '@/lib/api'
import { NoAccessPage } from '@/pages/NoAccessPage'
import type { SignInLogResponse } from './signInLogTypes'

type Sort = 'time' | 'person'
type Direction = 'ascending' | 'descending'
type Params = { search: string; outcome: string; sort: Sort; direction: Direction; page: number }
const outcomeOptions = [
  { value: 'signed_in', label: 'Signed in' },
  { value: 'refused', label: 'Refused' },
]

function readParams(): Params {
  const query = new URLSearchParams(window.location.search)
  const sort = query.get('sort') === 'person' ? 'person' : 'time'
  return {
    search: query.get('search') ?? '',
    outcome: query.get('outcome') ?? '',
    sort,
    direction:
      query.get('direction') === 'ascending'
        ? 'ascending'
        : query.get('direction') === 'descending'
          ? 'descending'
          : sort === 'person'
            ? 'ascending'
            : 'descending',
    page: Math.max(1, Number.parseInt(query.get('page') ?? '1', 10) || 1),
  }
}

function writeParams(value: Params) {
  const url = new URL(window.location.href)
  for (const name of ['search', 'outcome', 'sort', 'direction', 'page'])
    url.searchParams.delete(name)
  if (value.search) url.searchParams.set('search', value.search)
  if (value.outcome) url.searchParams.set('outcome', value.outcome)
  if (value.sort !== 'time') url.searchParams.set('sort', value.sort)
  if (value.direction !== (value.sort === 'time' ? 'descending' : 'ascending'))
    url.searchParams.set('direction', value.direction)
  if (value.page !== 1) url.searchParams.set('page', String(value.page))
  window.history.pushState({}, '', url)
}

export function ConnectionSignInLog({ connectionId }: { connectionId: number }) {
  const [params, setParams] = useState(readParams)
  useEffect(() => {
    const restore = () => setParams(readParams())
    window.addEventListener('popstate', restore)
    return () => window.removeEventListener('popstate', restore)
  }, [])
  function change(value: Partial<Params>, keepPage = false) {
    const next = { ...params, ...value, page: keepPage ? (value.page ?? params.page) : 1 }
    writeParams(next)
    setParams(next)
  }
  const query = useQuery({
    queryKey: ['connections', connectionId, 'sign_in_log', params],
    queryFn: () => {
      const search = new URLSearchParams()
      if (params.search) search.set('search', params.search)
      if (params.outcome) search.set('outcome', params.outcome)
      search.set('sort', params.sort)
      search.set('direction', params.direction)
      search.set('page', String(params.page))
      return getJSON<SignInLogResponse>(`/api/v1/connections/${connectionId}/sign_in_log?${search}`)
    },
    retry: false,
  })
  const response = query.data
  const pageCount = response ? Math.ceil(response.total / response.per_page) : 0
  function sort(key: Sort) {
    change({
      sort: key,
      direction:
        params.sort === key
          ? params.direction === 'ascending'
            ? 'descending'
            : 'ascending'
          : key === 'time'
            ? 'descending'
            : 'ascending',
    })
  }
  if (query.error instanceof ApiError && query.error.status === 403) return <NoAccessPage />
  return (
    <div className="space-y-4">
      <LogToolbar params={params} change={change} />
      {query.isError && <Alert>Could not load Sign-in log</Alert>}
      {query.isPending && (
        <p className="text-body-sm text-muted-foreground">Loading sign-in log…</p>
      )}
      <LogTable response={response} params={params} sort={sort} />
      {response && response.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-caption text-muted-foreground tabular-nums">
            {(response.page - 1) * response.per_page + 1}–
            {Math.min(response.page * response.per_page, response.total)} of {response.total}
          </span>
          <Pagination className="mx-0 w-auto">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  aria-disabled={response.page <= 1}
                  onClick={(event) => {
                    event.preventDefault()
                    if (response.page > 1) change({ page: response.page - 1 }, true)
                  }}
                />
              </PaginationItem>
              {Array.from({ length: pageCount }, (_, index) => index + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    href="#"
                    isActive={page === response.page}
                    onClick={(event) => {
                      event.preventDefault()
                      change({ page }, true)
                    }}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  href="#"
                  aria-disabled={response.page >= pageCount}
                  onClick={(event) => {
                    event.preventDefault()
                    if (response.page < pageCount) change({ page: response.page + 1 }, true)
                  }}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  )
}

function LogTable({
  response,
  params,
  sort,
}: {
  response?: SignInLogResponse
  params: Params
  sort: (key: Sort) => void
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead
              active={params.sort === 'time'}
              direction={params.sort === 'time' ? params.direction : 'descending'}
              initialDirection="descending"
              onToggle={() => sort('time')}
            >
              Time
            </SortableTableHead>
            <SortableTableHead
              active={params.sort === 'person'}
              direction={params.sort === 'person' ? params.direction : 'ascending'}
              onToggle={() => sort('person')}
            >
              Person
            </SortableTableHead>
            <TableHead>Outcome</TableHead>
            <TableHead>Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {response?.sign_in_log.map((row) => (
            <LogRow key={row.id} row={row} />
          ))}
        </TableBody>
      </Table>
      {response?.total === 0 && (
        <EmptyState
          title={params.search || params.outcome ? 'No sign-ins match' : 'No sign-ins yet'}
        />
      )}
    </div>
  )
}

function LogRow({ row }: { row: SignInLogResponse['sign_in_log'][number] }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap tabular-nums">{row.time}</TableCell>
      <TableCell>
        {row.person ? (
          <>
            <div className="text-body-sm">{row.person.name}</div>
            <div className="text-caption text-muted-foreground">{row.person.email}</div>
          </>
        ) : (
          <span className="text-body-sm text-muted-foreground">Unknown</span>
        )}
      </TableCell>
      <TableCell>
        <Badge variant={row.outcome === 'signed_in' ? 'success' : 'destructive'}>
          {row.outcome === 'signed_in' ? 'Signed in' : 'Refused'}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">{row.reason}</TableCell>
    </TableRow>
  )
}

function LogToolbar({
  params,
  change,
}: {
  params: Params
  change: (value: Partial<Params>) => void
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          strokeWidth={1.5}
          aria-hidden="true"
        />
        <Input
          className="w-64 pl-9"
          placeholder="Search name or email"
          aria-label="Search name or email"
          value={params.search}
          onChange={(event) => change({ search: event.target.value })}
        />
      </div>
      <FilterDropdown
        label="Outcome"
        emptyLabel="Outcome"
        options={outcomeOptions}
        selected={new Set(params.outcome ? [params.outcome] : [])}
        onChange={(selected) =>
          change({ outcome: [...selected].find((item) => item !== params.outcome) ?? '' })
        }
      />
      <span className="ml-auto text-caption text-muted-foreground">Kept for 90 days</span>
    </div>
  )
}
